import { useState } from 'react'
import { ImageOff, Play } from 'lucide-react'
import { cn } from '@/lib/cn'
import type { MediaItem } from '@/types'

/** YouTube's own poster frame for a video id. */
function posterFor(item: MediaItem): string | null {
  if (item.kind === 'image') return item.url
  return item.youtubeId ? `https://i.ytimg.com/vi/${item.youtubeId}/hqdefault.jpg` : null
}

/**
 * Shared by the card and the drawer, which is why it is its own file.
 *
 * Owns the broken-hotlink placeholder: hotlinked media 404s when a client
 * redesigns their site, and a browser broken-image icon tells the reviewer
 * nothing (docs/03-page-specs.md §7.4). Nothing records reachability yet, so
 * the browser's own load failure is the check.
 *
 * `no-referrer`, because hotlink protection on a client site keys on the
 * Referer. A video other than YouTube has no poster frame to show.
 */
export function MediaThumb({ item, className }: { item: MediaItem; className?: string }) {
  const [failed, setFailed] = useState<string | null>(null)
  const poster = posterFor(item)

  if (poster && failed === poster) {
    return (
      <div
        className={cn(
          'flex aspect-video flex-col items-center justify-center gap-1.5 rounded-[4px]',
          'bg-surface-sunk px-3 text-center',
          className,
        )}
      >
        <ImageOff size={20} className="text-ink-faint" aria-hidden />
        <p className="text-[11px] leading-4 text-ink-muted">The linked file could not be loaded</p>
      </div>
    )
  }

  return (
    <div
      className={cn(
        'relative flex aspect-video items-center justify-center overflow-hidden rounded-[4px] bg-[#3b3b3b]',
        className,
      )}
    >
      {poster && (
        <img
          src={poster}
          alt={item.description ?? item.title ?? ''}
          loading="lazy"
          referrerPolicy="no-referrer"
          onError={() => setFailed(poster)}
          className="absolute inset-0 h-full w-full object-contain"
        />
      )}
      {item.kind === 'video' && (
        <span className="relative flex h-10 w-10 items-center justify-center rounded-full bg-black/45 text-white">
          <Play size={16} fill="currentColor" aria-hidden />
        </span>
      )}
    </div>
  )
}
